import YAML from 'yaml';
import type {Deck, MediaRef, Slide, SlideMeta} from './model';
import {uniqueSlug} from './slug';
import {mapSlideBody} from './source-map';

const FRONTMATTER = /^---[ \t]*\r?\n([\s\S]*?)\r?\n---[ \t]*(?:\r?\n|$)/;
const H1 = /^#\s+(.+)$/m;
const NOTES = /(?:^|\n):::\s*notes\s*\n([\s\S]*?)\n:::\s*(?=\n|$)/i;
const MEDIA =
  /!\[([^\]]*)\]\((?:(animation|video):)?([^)\s"]+)(?:\s+"([^"]*)")?\)(?:\{([^}]*)\})?/g;

type Chunk = {start: number; end: number; raw: string};

function fencedLineStarts(source: string): Set<number> {
  const fenced = new Set<number>();
  let active: {marker: '`' | '~'; length: number} | undefined;
  let offset = 0;
  for (const line of source.split(/\r?\n/)) {
    const marker = /^\s{0,3}(`{3,}|~{3,})/.exec(line)?.[1];
    if (active) fenced.add(offset);
    if (marker) {
      const kind = marker[0] as '`' | '~';
      if (!active) {
        active = {marker: kind, length: marker.length};
        fenced.add(offset);
      } else if (kind === active.marker && marker.length >= active.length) {
        active = undefined;
      }
    }
    offset += line.length + (source.slice(offset + line.length, offset + line.length + 2) === '\r\n' ? 2 : 1);
  }
  return fenced;
}

/** Keep offsets intact while excluding fenced examples from deck metadata. */
function withoutFencedCode(source: string): string {
  const fenced = fencedLineStarts(source);
  let offset = 0;
  return source.split('\n').map(line => {
    const value = fenced.has(offset) ? ' '.repeat(line.length) : line;
    offset += line.length + 1;
    return value;
  }).join('\n');
}

function findSlides(source: string): Chunk[] {
  const starts: number[] = [];
  const closings = new Set<number>();
  const fenced = fencedLineStarts(source);
  const lines = [...source.matchAll(/^---[ \t]*\r?$/gm)].filter(
    (match) => !fenced.has(match.index ?? 0),
  );
  for (const match of lines) {
    const start = match.index ?? 0;
    if (closings.has(start)) continue;
    const tail = source.slice(start);
    const frontmatter = FRONTMATTER.exec(tail);
    if (!frontmatter) continue;
    try {
      const parsed = YAML.parse(frontmatter[1]);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        starts.push(start);
        closings.add(start + frontmatter[0].lastIndexOf('---'));
      }
    } catch {
      // Keep a malformed metadata block as a boundary so editing reports
      // the YAML error instead of silently merging it into the prior slide.
      if (/^[\w-]+\s*:/.test(frontmatter[1])) {
        starts.push(start);
        closings.add(start + frontmatter[0].lastIndexOf('---'));
      }
    }
  }
  for (const match of lines) {
    const start = match.index ?? 0;
    if (!closings.has(start) && /^\r?\n#\s/.test(source.slice(start + match[0].length))) {
      starts.push(start);
    }
  }
  const uniqueStarts = [...new Set(starts)].sort((a, b) => a - b);
  starts.splice(0, starts.length, ...uniqueStarts);

  if (starts.length === 0) return [{start: 0, end: source.length, raw: source}];
  if (starts[0] > 0 && source.slice(0, starts[0]).trim()) starts.unshift(0);

  return starts.map((start, index) => {
    const end = starts[index + 1] ?? source.length;
    return {start, end, raw: source.slice(start, end).trimEnd()};
  });
}

function parseAttrs(raw = ''): Record<string, string | boolean> {
  const attrs: Record<string, string | boolean> = {};
  const pattern = /([\w-]+)(?:=(?:"([^"]*)"|'([^']*)'|([^\s]+)))?/g;
  for (const match of raw.matchAll(pattern)) {
    attrs[match[1]] = match[2] ?? match[3] ?? match[4] ?? true;
  }
  return attrs;
}

export function parseMedia(markdown: string): MediaRef[] {
  return [...withoutFencedCode(markdown).matchAll(MEDIA)].map((match) => {
    const prefix = match[2];
    const source = match[3];
    const kind =
      prefix === 'animation'
        ? 'animation'
        : prefix === 'video' || /\.(mp4|webm|mov)(?:[?#]|$)/i.test(source)
          ? 'video'
          : 'image';
    return {
      kind,
      source,
      alt: match[1],
      title: match[4],
      attrs: parseAttrs(match[5]),
    };
  });
}

function revealCount(markdown: string): number {
  let count = 0;
  const fences = fencedLineStarts(markdown);
  let offset = 0;
  const lines = markdown.split('\n').map(line => {
    const skip = fences.has(offset);
    offset += line.length + 1;
    return skip ? '' : line.replace(/\r$/, '');
  });
  for (const [index, line] of lines.entries()) {
    if (/^:panel\s+.*\breveal\b/i.test(line.trim())) {
      count += 1;
      continue;
    }
    const match = /^:reveal(?:\s+(next|each|\d+))?\s*$/i.exec(line.trim());
    if (!match) continue;
    if (/^\d+$/.test(match[1] ?? '')) {
      count = Number(match[1]) > 0 ? Math.max(count, Number(match[1])) : count + 1;
    } else {
      count += 1;
      if (match[1]?.toLowerCase() === 'each') {
        let items = 0;
        let media = 0;
        for (const candidate of lines.slice(index + 1)) {
          if (!candidate.trim() && items === 0 && media === 0) continue;
          if (/^\s*(?:[-*+]|\d+[.)])\s+/.test(candidate)) {
            items += 1;
            continue;
          }
          if (/^\s*!\[[^\]]*\]\(/.test(candidate)) {
            media += 1;
            continue;
          }
          if (/^\s*:row\s*$/i.test(candidate) && media > 0) continue;
          if (candidate.trim()) break;
        }
        count += Math.max(0, Math.max(items, media) - 1);
      }
    }
  }
  return count;
}

function splitNotes(markdown: string) {
  const fences = fencedLineStarts(markdown);
  const match = [...markdown.matchAll(new RegExp(NOTES.source, 'gi'))].find(
    entry => !fences.has(entry.index + (entry[0].startsWith('\n') ? 1 : 0)),
  );
  if (!match) return {...mapSlideBody(markdown), notes: ''};
  return {
    ...mapSlideBody(markdown, {from: match.index, to: match.index + match[0].length}),
    notes: match[1].trim(),
  };
}

function parseChunk(chunk: Chunk, index: number, used: Map<string, number>): Slide {
  const frontmatter = FRONTMATTER.exec(chunk.raw);
  let meta: SlideMeta = {};
  let bodyStart = 0;

  if (frontmatter) {
    meta = (YAML.parse(frontmatter[1]) ?? {}) as SlideMeta;
    bodyStart = frontmatter[0].length;
  } else if (/^---[ \t]*\r?\n#\s/.test(chunk.raw)) {
    bodyStart = chunk.raw.indexOf('\n') + 1;
  }

  const sourceBody = chunk.raw.slice(bodyStart);
  const {body, notes, spans} = splitNotes(sourceBody);
  const title = H1.exec(withoutFencedCode(body))?.[1]?.trim() ?? `Slide ${index + 1}`;
  return {
    index,
    id: uniqueSlug(title, used),
    title,
    meta,
    markdown: body,
    notes,
    sourceSpans: spans.map(span => ({...span, sourceFrom: chunk.start + bodyStart + span.sourceFrom})),
    range: {
      start: chunk.start,
      end: chunk.end,
      bodyStart: chunk.start + bodyStart,
    },
    media: parseMedia(body),
    revealCount: revealCount(body),
  };
}

export function parseDeck(source: string): Deck {
  const used = new Map<string, number>();
  const slides = findSlides(source)
    .filter((chunk) => chunk.raw.trim())
    .map((chunk, index) => parseChunk(chunk, index, used));
  const firstTitle = slides[0]?.title ?? 'Untitled deck';
  const title = String(slides[0]?.meta.deck ?? firstTitle);
  const theme = String(slides[0]?.meta.theme ?? 'default');
  const rawStyle = slides[0]?.meta.style;
  const style =
    rawStyle && typeof rawStyle === 'object' && !Array.isArray(rawStyle)
      ? Object.fromEntries(
          Object.entries(rawStyle).flatMap(([key, value]) =>
            typeof value === 'string' ? [[key, value]] : [],
          ),
        )
      : {};
  const rawChains = slides[0]?.meta.chains;
  const chains =
    rawChains && typeof rawChains === 'object' && !Array.isArray(rawChains)
      ? Object.fromEntries(
          Object.entries(rawChains).flatMap(([id, nodes]) =>
            Array.isArray(nodes) && nodes.every((node) => typeof node === 'string')
              ? [[id, nodes]]
              : [],
          ),
        )
      : {};
  const language = String(slides[0]?.meta.lang ?? 'en');
  return {title, language, theme, style, source, slides, chains};
}

export function slideAtOffset(deck: Deck, offset: number): number {
  const index = deck.slides.findIndex(
    (slide) => offset >= slide.range.start && offset < slide.range.end,
  );
  return index >= 0 ? index : Math.max(0, deck.slides.length - 1);
}
